// Single source of truth for every categorical/ordinal field on profiles.
// Imported by forms (dropdown options + Zod schemas), by the matching algorithm
// (ordinal compare + set membership), and mirrored by DB CHECK constraints in
// `supabase/migrations/20260518120000_enum_check_constraints.sql`.
//
// Ordinal enums are ordered low → high; the array index is the rank.
// Categorical enums are unordered (set membership only).
//
// Seeking-side variants append a "No preference" sentinel. The self-side fields
// keep their own opt-out (e.g. "Prefer not to say") because that's semantically
// distinct from "no preference for a partner."
//
// IMPORTANT: When changing any of these lists, you must also update the DB
// CHECK constraint migration in the same change. Otherwise writes will fail or
// silently drift.

// ============================================================================
// ORDINAL ENUMS (index = rank, lowest first)
// ============================================================================

export const MASJID_ATTENDANCE = [
  "Never",
  "Rarely",
  "Major holidays",
  "Jumuah sometimes",
  "Jumuah regularly",
  "5x prayers at masjid",
  "Daily",
] as const;
export type MasjidAttendance = (typeof MASJID_ATTENDANCE)[number];

export const MASJID_ATTENDANCE_SEEKING = [
  "Daily",
  "5x prayers at masjid",
  "Jumuah regularly",
  "Jumuah sometimes",
  "Major holidays",
  "No preference",
] as const;
export type MasjidAttendanceSeeking = (typeof MASJID_ATTENDANCE_SEEKING)[number];

export const PRAYER_FREQUENCY = [
  "working on it",
  "sometimes",
  "most prayers",
  "5 daily",
] as const;
export type PrayerFrequency = (typeof PRAYER_FREQUENCY)[number];

export const RELIGIOSITY_LEVEL = ["learning", "moderate", "practicing"] as const;
export type ReligiosityLevel = (typeof RELIGIOSITY_LEVEL)[number];

export const RELIGIOSITY_LEVEL_SEEKING = [
  "learning",
  "moderate",
  "practicing",
  "No preference",
] as const;
export type ReligiosityLevelSeeking = (typeof RELIGIOSITY_LEVEL_SEEKING)[number];

export const HIJAB_LEVEL = [
  "No",
  "No, but planning to",
  "Sometimes",
  "Yes, usually",
  "Yes, always",
] as const;
export type HijabLevel = (typeof HIJAB_LEVEL)[number];

export const BEARD_LEVEL = [
  "Clean shaven",
  "Goatee/mustache",
  "Yes, trimmed",
  "Yes, Sunnah length",
] as const;
export type BeardLevel = (typeof BEARD_LEVEL)[number];

export const HIJAB_PREFERENCE = [
  "No preference",
  "Prefer no",
  "Prefer yes",
  "Must wear",
] as const;
export type HijabPreference = (typeof HIJAB_PREFERENCE)[number];

export const BEARD_PREFERENCE = [
  "No preference",
  "Clean shaven",
  "Light beard",
  "Full beard",
] as const;
export type BeardPreference = (typeof BEARD_PREFERENCE)[number];

// Education is ordered, but two values ("Trade/Vocational", "Other") don't
// fit cleanly on the standard academic ladder. We expose both: a canonical
// list (for form options + DB CHECK) and an EDUCATION_ORDINAL map for the
// algorithm to look up a numeric rank. Unranked values map to 0 (algo treats
// 0 as "no ordinal compare possible" and falls back to set membership).
export const EDUCATION_LEVEL = [
  "High School",
  "Trade/Vocational",
  "Associate's",
  "Bachelor's Degree",
  "Master's Degree",
  "PhD/Doctorate",
  "Other",
] as const;
export type EducationLevel = (typeof EDUCATION_LEVEL)[number];

export const EDUCATION_ORDINAL: Record<EducationLevel, number> = {
  "High School": 1,
  "Trade/Vocational": 2,
  "Associate's": 3,
  "Bachelor's Degree": 4,
  "Master's Degree": 5,
  "PhD/Doctorate": 6,
  Other: 0,
};

export const EDUCATION_LEVEL_SEEKING = [
  ...EDUCATION_LEVEL,
  "No preference",
] as const;
export type EducationLevelSeeking = (typeof EDUCATION_LEVEL_SEEKING)[number];

export const EXERCISE_FREQUENCY = [
  "Never",
  "Rarely",
  "Weekly",
  "Several times a week",
  "Daily",
] as const;
export type ExerciseFrequency = (typeof EXERCISE_FREQUENCY)[number];

export const QURAN_KNOWLEDGE = [
  "Want to improve",
  "Basic",
  "Learning",
  "Can recite fluently",
  "Hafiz/Hafiza",
] as const;
export type QuranKnowledge = (typeof QURAN_KNOWLEDGE)[number];

export const ISLAMIC_KNOWLEDGE_LEVEL = [
  "Learning",
  "Basic",
  "Moderate",
  "Self-taught/advanced",
  "Studied formally",
] as const;
export type IslamicKnowledgeLevel = (typeof ISLAMIC_KNOWLEDGE_LEVEL)[number];

// ============================================================================
// CATEGORICAL ENUMS (no order — set membership only)
// ============================================================================

export const SECT = ["Sunni", "Shia", "Just Muslim", "Other", "Prefer not to say"] as const;
export type Sect = (typeof SECT)[number];

export const SECT_SEEKING = ["Sunni", "Shia", "Just Muslim", "Other", "No preference"] as const;
export type SectSeeking = (typeof SECT_SEEKING)[number];

export const ETHNICITY = [
  "Arab",
  "South Asian",
  "African American",
  "African",
  "Southeast Asian",
  "Turkish/Central Asian",
  "White/Caucasian",
  "Hispanic/Latino",
  "Mixed",
  "Other",
  "Prefer not to say",
] as const;
export type Ethnicity = (typeof ETHNICITY)[number];

export const ETHNICITY_SEEKING = [
  "Arab",
  "South Asian",
  "African American",
  "African",
  "Southeast Asian",
  "Turkish/Central Asian",
  "White/Caucasian",
  "Hispanic/Latino",
  "Mixed",
  "Other",
  "No preference",
] as const;
export type EthnicitySeeking = (typeof ETHNICITY_SEEKING)[number];

// Single canonical casing — Step1 used "Never Married", Step5 had "Never married"
// (lowercase m) which silently broke algo matching. Picked the cap-M version since
// that's what existing profile rows already have on prod.
export const MARITAL_STATUS = ["Never Married", "Divorced", "Widowed", "Separated"] as const;
export type MaritalStatus = (typeof MARITAL_STATUS)[number];

export const MARITAL_STATUS_SEEKING = [
  "Never Married",
  "Divorced",
  "Widowed",
  "No preference",
] as const;
export type MaritalStatusSeeking = (typeof MARITAL_STATUS_SEEKING)[number];

export const CITIZENSHIP_STATUS = [
  "US Citizen",
  "Permanent Resident",
  "Work Visa",
  "Student Visa",
  "Other",
] as const;
export type CitizenshipStatus = (typeof CITIZENSHIP_STATUS)[number];

export const CITIZENSHIP_STATUS_SEEKING = [
  "US Citizen",
  "Permanent Resident",
  "Work Visa",
  "Student Visa",
  "No preference",
] as const;
export type CitizenshipStatusSeeking = (typeof CITIZENSHIP_STATUS_SEEKING)[number];

export const SMOKING = [
  "Never",
  "Socially",
  "Regularly",
  "Trying to quit",
  "Prefer not to say",
] as const;
export type Smoking = (typeof SMOKING)[number];

export const EMPLOYMENT_STATUS = [
  "Employed full-time",
  "Employed part-time",
  "Self-employed",
  "Student",
  "Homemaker",
  "Unemployed",
  "Retired",
] as const;
export type EmploymentStatus = (typeof EMPLOYMENT_STATUS)[number];

export const MUSLIM_BACKGROUND = ["Born Muslim", "Revert"] as const;
export type MuslimBackground = (typeof MUSLIM_BACKGROUND)[number];

export const DIETARY_PREFERENCES = [
  "Strictly Halal",
  "Halal when possible",
  "Vegetarian",
  "Pescatarian",
  "No restrictions",
] as const;
export type DietaryPreference = (typeof DIETARY_PREFERENCES)[number];

// DFW-only for now. Expand when/if MAS Matrimonial opens to other chapters.
export const LOCATION_DFW = [
  "Richardson",
  "Dallas",
  "Plano",
  "Frisco",
  "Irving",
  "Arlington",
  "Fort Worth",
  "Garland",
  "McKinney",
  "Other",
] as const;
export type LocationDFW = (typeof LOCATION_DFW)[number];

// NOTE: seeking_location_preference is collected by the form but the matching
// algorithm currently IGNORES it entirely (it uses profile.location string
// equality + willing_to_relocate boolean). Listed here for the form + DB CHECK
// only; the algorithm would need to be extended to consume this.
export const SEEKING_LOCATION_PREFERENCE = [
  "Must be in same city",
  "Same state/region",
  "Anywhere in US",
  "Open to relocation",
  "International OK",
  "No preference",
] as const;
export type SeekingLocationPreference = (typeof SEEKING_LOCATION_PREFERENCE)[number];

export const PREFERRED_CONTACT_METHOD = ["Email", "Phone", "WhatsApp"] as const;
export type PreferredContactMethod = (typeof PREFERRED_CONTACT_METHOD)[number];

// ============================================================================
// IMPORTANCE WEIGHTS (used by seeking_preferences_weights)
// ============================================================================

export const IMPORTANCE_LEVEL = [
  "dont_care",
  "flexible",
  "somewhat_important",
  "important",
  "very_important",
  "deal_breaker",
] as const;
export type ImportanceLevel = (typeof IMPORTANCE_LEVEL)[number];

export const IMPORTANCE_MULTIPLIERS: Record<ImportanceLevel, number> = {
  deal_breaker: Infinity,
  very_important: 2.0,
  important: 1.5,
  somewhat_important: 1.0,
  flexible: 0.5,
  dont_care: 0.0,
};

// ============================================================================
// HELPERS
// ============================================================================

// Strings that mean "I don't have a preference here" — used on either the self
// side (Prefer not to say) or the seeking side (No preference). The algorithm
// treats both as flexible/pass-through.
export const NO_PREFERENCE_SENTINELS = ["No preference", "Prefer not to say"] as const;

export function isNoPreference(value: string | null | undefined): boolean {
  if (value == null) return false;
  return (NO_PREFERENCE_SENTINELS as readonly string[]).includes(value);
}

// Ordinal lookup against a canonical list. Returns the index (0-based rank) or
// null if value is missing/unrecognized. Logs a console warning on miss so
// drifted data surfaces in the console + Sentry rather than silently scoring 0.
//
// fieldName is used purely for the warning message.
export function ordinalIndex<T extends readonly string[]>(
  list: T,
  value: string | null | undefined,
  fieldName: string,
): number | null {
  if (value == null || value === "") return null;
  if (isNoPreference(value)) return null; // sentinel — caller decides what to do
  const idx = (list as readonly string[]).indexOf(value);
  if (idx === -1) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn(
        `[enums] Unknown value "${value}" for field "${fieldName}". ` +
          `Allowed: ${list.join(" | ")}. Returning null (dimension will be skipped).`,
      );
    }
    return null;
  }
  return idx;
}

// True if value is in the canonical list (or null/empty — caller decides).
export function isValidEnumValue<T extends readonly string[]>(
  list: T,
  value: string | null | undefined,
): boolean {
  if (value == null || value === "") return true; // null is "not set", not invalid
  return (list as readonly string[]).includes(value);
}

// Education uses a separate ordinal map because two values ("Other",
// "Trade/Vocational") don't sit on the academic ladder cleanly. Returns the
// rank or null if value isn't known.
export function educationOrdinal(
  value: string | null | undefined,
): number | null {
  if (value == null || value === "") return null;
  if (isNoPreference(value)) return null;
  if (!(value in EDUCATION_ORDINAL)) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn(
        `[enums] Unknown value "${value}" for field "education_level". ` +
          `Allowed: ${EDUCATION_LEVEL.join(" | ")}. Returning null.`,
      );
    }
    return null;
  }
  const rank = EDUCATION_ORDINAL[value as EducationLevel];
  return rank === 0 ? null : rank; // 0 = unranked (e.g. "Other")
}
